import Dialog from '@material-ui/core/Dialog';
import Fade from '@material-ui/core/Fade';
import { AlertMessage } from 'components/Alert';
import moment from "moment";
import React, { useEffect, useState } from 'react';
import { Calendar, momentLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "./EventsCalendar.scss";
import EventCalendarItem from './EventCalendarItem';
import EventsList from 'components/EventsList';
import { CopyAlert, EventsCalendarContainer } from './Theme';
import { Fonts, FontColors } from "theme/fontConfig";

const localizer = momentLocalizer(moment);

const EventTile = ({ event }) => (
  <span style={{
    display: "block",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    ...Fonts.GeneralSmallLabelFont,
    lineHeight: "1.4",
  }}>
    {event.title}
  </span>
);

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 768px)').matches);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return isMobile;
};

const EventsCalendar = ({ events, withoutTitle, width, showList = true }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [copyLinkSuccess, setCopyLinkSuccess] = React.useState(false);
  const [mobileView, setMobileView] = useState('list');
  const isMobile = useIsMobile();

  useEffect(() => {
    const timer = setTimeout(() => setCopyLinkSuccess(false), 2000);
    return () => clearTimeout(timer);
  }, [copyLinkSuccess]);

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setIsOpen(true);
  };

  return (
    <EventsCalendarContainer>
      {!withoutTitle && (
        <div style={{
          ...Fonts.GeneralHeaderFont,
          color: FontColors.Slate0F172A,
          marginBottom: 20,
          paddingLeft: isMobile ? 0 : 8,
          textAlign: isMobile ? "center" : "left",
        }}>
          Events Calendar
        </div>
      )}

      {!isMobile ? (
        /* Desktop layout */
        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', width: '100%' }}>
          {showList && (
            <div style={{ width: 320, flexShrink: 0 }}>
              <EventsList events={events} onEventClick={handleEventClick} />
            </div>
          )}
          <div className="rbc-calendar-shell" style={{ flex: 1, minWidth: 0, position: "sticky", top: 24, alignSelf: "flex-start" }}>
            <Calendar
              views={["day", "week", "month"]}
              selectable
              localizer={localizer}
              defaultDate={new Date()}
              defaultView="month"
              events={events}
              style={{ height: 620 }}
              components={{ event: EventTile }}
              onSelectEvent={handleEventClick}
              eventPropGetter={(event) => ({
                className: "",
                style: {
                  backgroundColor: event.isMine ? "#10B981" : "#2555EF",
                  color: FontColors.White,
                  border: "none",
                },
              })}
            />
          </div>
        </div>
      ) : (
        /* Mobile layout */
        <>
          {showList && (
            <div style={{ display: 'flex', border: '1px solid #E2E8F0', borderRadius: 10, overflow: 'hidden', marginBottom: 16, width: '100%' }}>
              <button
                onClick={() => setMobileView('list')}
                style={{ flex: 1, padding: '8px 0', border: 'none', ...Fonts.GeneralButtonFont, cursor: 'pointer', background: mobileView === 'list' ? '#2555EF' : 'transparent', color: mobileView === 'list' ? '#fff' : '#475569', transition: 'background 0.15s' }}
              >
                Upcoming
              </button>
              <button
                onClick={() => setMobileView('calendar')}
                style={{ flex: 1, padding: '8px 0', border: 'none', borderLeft: '1px solid #E2E8F0', ...Fonts.GeneralButtonFont, cursor: 'pointer', background: mobileView === 'calendar' ? '#2555EF' : 'transparent', color: mobileView === 'calendar' ? '#fff' : '#475569', transition: 'background 0.15s' }}
              >
                Calendar
              </button>
            </div>
          )}

          {showList && mobileView === 'list' ? (
            <EventsList events={events} onEventClick={handleEventClick} />
          ) : (
            <div className="rbc-calendar-shell" style={{ width: '100%' }}>
              <Calendar
                views={["day", "week", "month"]}
                selectable
                localizer={localizer}
                defaultDate={new Date()}
                defaultView="month"
                events={events}
                style={{ height: 520 }}
                components={{ event: EventTile }}
                onSelectEvent={handleEventClick}
                eventPropGetter={(event) => ({
                  className: "",
                  style: {
                    backgroundColor: event.isMine ? "#10B981" : "#2555EF",
                    color: FontColors.White,
                    border: "none",
                  },
                })}
              />
            </div>
          )}
        </>
      )}

      <Dialog
        className="my-modal"
        onClose={() => { setIsOpen(false); setSelectedEvent(null); }}
        open={isOpen}
        PaperProps={{ style: { borderRadius: 16, overflow: "hidden", margin: 16 } }}
      >
        <Fade in={isOpen}>
          <EventCalendarItem
            event={selectedEvent}
            setCopyLinkSuccess={setCopyLinkSuccess}
          />
        </Fade>
      </Dialog>

      {copyLinkSuccess && (
        <CopyAlert>
          <AlertMessage type="success" content="URL Copied Successfully" />
        </CopyAlert>
      )}
    </EventsCalendarContainer>
  );
};

export default EventsCalendar;
